import { useBoolean } from "@/hooks/use-boolean";
import useFormatPrice from "@/hooks/use-format-price.ts";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import { Carrier } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Alert, AlertDescription } from "@babackapp/ui/components/alert";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";
import { useCarriersContext } from "../../use-carriers-context";
import { AddCarrierTransportDialog } from "../forms/add-carrier-transport-dialog";
import { DeleteCarrierTransportDialog } from "../forms/delete-carrier-transport-dialog";
import { EditCarrierTransportDialog } from "../forms/edit-carrier-transport-dialog";
import { extractCarrierTagValue } from "../lib/tag-conditions.helpers";
import { PricingByCarrier } from "./pricing-by-carrier";
import { CarrierListItem } from "./carrier-list-item";

export const CarriersPricings = () => {
  const { shop } = useCarriersContext();
  const { carriers } = useCarriersContext();
  const formatPrice = useFormatPrice();
  const [selectedTransport, setSelectedTransport] = useState<
    Carrier | undefined
  >(undefined);
  const hasAdminRights = useViewerHasAtLeastAdminRights();

  const [isEditing, setIsEditing] = useState(false);
  const editTransportationModale = useBoolean(false);
  const onCloseEditingTransportation = useMemo(() => {
    return () => {
      editTransportationModale.setFalse();
      setSelectedTransport(undefined);
    };
  }, []);

  const onEditCarrierTransport = (selectedTransportID: string | undefined) => {
    setSelectedTransport(carriers.find((c) => c.id === selectedTransportID));
    editTransportationModale.setTrue();
  };

  const deleteTransportationModale = useBoolean(false);
  const onCloseDeleteTransportation = useMemo(() => {
    return () => {
      deleteTransportationModale.setFalse();
      setSelectedTransport(undefined);
    };
  }, []);
  const onDeleteCarrierTransport = (
    selectedTransportID: string | undefined,
  ) => {
    setSelectedTransport(carriers.find((c) => c.id === selectedTransportID));
    deleteTransportationModale.setTrue();
  };

  const t = useTranslation();

  const allCarriersHaveConditionalTags = useMemo(() => {
    if (carriers.length === 0) return false;

    return carriers.every((carrier) => {
      const tagValue = extractCarrierTagValue(carrier.conditionalRules);
      return tagValue !== null;
    });
  }, [carriers]);

  if (!shop) {
    return <></>;
  }

  return (
    <>
      {selectedTransport && editTransportationModale.value && (
        <EditCarrierTransportDialog
          selectedCarrier={selectedTransport}
          onClose={onCloseEditingTransportation}
          setIsModalOpen={editTransportationModale.setValue}
          isOpen={editTransportationModale.value}
        />
      )}
      {selectedTransport && deleteTransportationModale.value && (
        <DeleteCarrierTransportDialog
          selectedCarrierID={selectedTransport?.id}
          closeForm={onCloseDeleteTransportation}
          setIsModalOpen={deleteTransportationModale.setValue}
          isOpen={deleteTransportationModale.value}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle> {t("settings.carriers.title")}</CardTitle>
          <CardDescription>
            <div className="flex flex-row justify-between">
              <div>
                {t("carrier.carrierSubBtnLabel", {
                  count: carriers.length,
                })}
              </div>
              <div>
                {shop &&
                  hasAdminRights &&
                  !shop.plan.transportManagedByBB.activated && (
                    <AddCarrierTransportDialog>
                      <Button variant="default" size="sm">
                        {t("carrier.addCarrier")}
                      </Button>
                    </AddCarrierTransportDialog>
                  )}
              </div>
            </div>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {allCarriersHaveConditionalTags && (
            <Alert className="mb-4 border-yellow-200 bg-yellow-50">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                {t("carrier.tagCondition.warning")}
              </AlertDescription>
            </Alert>
          )}
          {!shop.plan.transportManagedByBB.activated && carriers.length > 0 && (
            <div className="space-y-6 pt-6 ">
              {carriers.map((carrier) => (
                <CarrierListItem
                  key={carrier.id}
                  carrier={carrier}
                  shopId={shop.id}
                  onEdit={onEditCarrierTransport}
                  onDelete={onDeleteCarrierTransport}
                />
              ))}
            </div>
          )}

          <>
            {!isEditing && (
              <div className="flex justify-between items-center">
                <h3 className="w-52 text-sm">
                  {t("settings.carriers.transportCostForRefund")}
                </h3>
                <div className="text-sm">
                  {carriers.map((carrier) => (
                    <div key={carrier.id}>
                      {carrier.pricings.map(
                        (pricing, index) =>
                          `${index ? ", " : ""} ${pricing.origin} ${formatPrice(
                            {
                              amount: pricing.price,
                              currency: pricing.currency,
                            },
                          )}`,
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary w-20"
                  onClick={() =>
                    shop && hasAdminRights ? setIsEditing(true) : null
                  }
                >
                  {t("misc.update")}
                </Button>
              </div>
            )}
            {isEditing && (
              <>
                {carriers.map((carrier) => (
                  <PricingByCarrier carrier={carrier} key={carrier.id} />
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                  }}
                >
                  {t("misc.close")}
                </Button>
              </>
            )}
          </>
        </CardContent>
      </Card>
    </>
  );
};
